import React, { useEffect, useMemo } from 'react'
import { Grid } from '@mui/material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { fetchMe } from '../../../store/slices/users/slice'
import { buildAccess } from '../../../core/helpers/access'
import { PageWrapper, SettingsHeader, SettingsTitle, TabsBody, TabButton } from './styled'
import { SETTINGS_ACTIONS_ID } from '../settings-header-action/settings-header-action'

const getSettingsSectionKey = (pathname) => {
  const parts = String(pathname || '').split('/').filter(Boolean)
  if (parts[0] !== 'settings') return 'profile'
  if (parts[1] === 'companies' && parts[3] === 'payment-methods') return 'payment-methods'
  return parts[1] || 'profile'
}

const SettingsLayout = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { currentUser } = useSelector(state => state.users)
  const capabilities = useSelector(state => state.capabilities)
  const canSeeTelegram = buildAccess(currentUser, capabilities).can('telegram_linked_chat')

  useEffect(() => {
    if (!currentUser) dispatch(fetchMe())
  }, [dispatch, currentUser])

  const tabs = useMemo(() => {
    const base = [
      { key: 'profile', label: t('settings.tabs.profile', 'Профиль'), to: '/settings/profile' },
      // «Подписка», а не «Тариф»: вкладка «Тарифы» ниже - это тарифы аренды
      // для курьеров (CompanyRate), совсем другая сущность
      { key: 'subscription', label: t('settings.tabs.subscription', 'Подписка'), to: '/settings/subscription' },
    ]

    if (canSeeTelegram) {
      base.push({
        key: 'telegram',
        label: t('settings.tabs.notifications', 'Уведомления'),
        to: '/settings/telegram',
      })
    }

    base.push(
      { key: 'companies', label: t('companies.sidebarTitle', 'Компании'), to: '/settings/companies' },
      {
        key: 'payment-methods',
        label: t('paymentMethods.title', 'Способы оплаты'),
        to: '/settings/payment-methods',
      },
      { key: 'client-hubs', label: t('clientHubs.sidebarTitle', 'Точки'), to: '/settings/client-hubs' },
      { key: 'geofences', label: t('geofences.sidebarTitle', 'Геозоны'), to: '/settings/geofences' },
      { key: 'contract-templates', label: t('geofences.contractTemplatesTitle', 'Шаблоны документов'), to: '/settings/contract-templates' },
      { key: 'rates', label: t('rates.sidebarTitle', 'Тарифы'), to: '/settings/rates' }
    )

    return base
  }, [t, canSeeTelegram])

  const activeKeyRaw = getSettingsSectionKey(location.pathname)
  const activeKey = tabs.some(x => x.key === activeKeyRaw) ? activeKeyRaw : (tabs[0]?.key || 'profile')

  const handleTabClick = (to) => {
    if (!to) return
    navigate(to)
  }

  return (
    <PageWrapper>
      <SettingsHeader>
        <SettingsTitle>{t('settings.title', 'Настройки')}</SettingsTitle>
        {/* Сюда страницы вкладок выводят свою главную кнопку */}
        <div id={SETTINGS_ACTIONS_ID} style={{ display: 'flex', gap: 8, alignItems: 'center' }} />
      </SettingsHeader>

      <TabsBody>
        <Grid container rowSpacing={0} columnSpacing={3}>
          {tabs.map(tab => (
            <Grid item xs="auto" key={tab.key}>
              <TabButton
                isActive={activeKey === tab.key}
                onClick={() => handleTabClick(tab.to)}
              >
                {tab.label}
              </TabButton>
            </Grid>
          ))}
        </Grid>
      </TabsBody>

      <Outlet />
    </PageWrapper>
  )
}

export default SettingsLayout
